import { io } from 'socket.io-client';
import { MODULES } from 'figshelf-modules';

const { Constants } = MODULES;
const { constants } = Constants;

export const ADMIN_SOCKET_NAMESPACE = 'admin';

export const ADMIN_SOCKET_EVENTS = {
  FIGURE_DRAFT_SUBMITTED: 'admin.figureDraft.submitted',
  FIGURE_DATABASE_DRAFT_SUBMITTED: 'admin.figureDatabaseDraft.submitted',
  COMPANY_DRAFT_SUBMITTED: 'admin.companyDraft.submitted',
  REPORT_CREATED: 'admin.report.created',
};

class AdminSocketService {
  static instance;

  static getInstance() {
    if (!AdminSocketService.instance) {
      AdminSocketService.instance = new AdminSocketService();
    }
    return AdminSocketService.instance;
  }

  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
    this.connectionStateListeners = new Set();
    this.badgeListeners = new Set();
    this.badges = {
      pendingCompanies: 0,
      pendingFigures: 0,
      pendingFigureDatabaseDrafts: 0,
      pendingReports: 0,
    };
  }

  getToken() {
    return localStorage.getItem('@auth_token');
  }

  connect() {
    if (this.socket) return this.socket;

    const token = this.getToken();
    if (!token) {
      return null;
    }

    const socketUrl = constants.SOCKET_URL;
    const fullSocketUrl = socketUrl.startsWith('/') ? `${constants.API_URL}${socketUrl}` : socketUrl;

    this.socket = io(fullSocketUrl, {
      auth: { token },
      query: { namespace: ADMIN_SOCKET_NAMESPACE },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      this.connected = true;
      this.notifyConnectionState();
      this.listeners.forEach((callbacks, event) => {
        callbacks.forEach((cb) => this.socket.on(event, cb));
      });
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
      this.notifyConnectionState();
    });

    this.socket.on(ADMIN_SOCKET_EVENTS.COMPANY_DRAFT_SUBMITTED, () => {
      this.updateBadges({ pendingCompanies: this.badges.pendingCompanies + 1 });
    });

    this.socket.on(ADMIN_SOCKET_EVENTS.FIGURE_DRAFT_SUBMITTED, () => {
      this.updateBadges({ pendingFigures: this.badges.pendingFigures + 1 });
    });

    this.socket.on(ADMIN_SOCKET_EVENTS.FIGURE_DATABASE_DRAFT_SUBMITTED, () => {
      this.updateBadges({ pendingFigureDatabaseDrafts: this.badges.pendingFigureDatabaseDrafts + 1 });
    });

    this.socket.on(ADMIN_SOCKET_EVENTS.REPORT_CREATED, () => {
      this.updateBadges({ pendingReports: this.badges.pendingReports + 1 });
    });

    return this.socket;
  }

  notifyConnectionState() {
    this.connectionStateListeners.forEach((cb) => {
      try {
        cb({ connected: this.connected });
      } catch (_) {}
    });
  }

  onConnectionState(callback) {
    this.connectionStateListeners.add(callback);
    try {
      callback({ connected: this.connected });
    } catch (_) {}
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  getBadges() {
    return { ...this.badges };
  }

  setBadges(nextBadges) {
    this.badges = {
      ...this.badges,
      ...(nextBadges || {}),
    };
    this.notifyBadges();
  }

  updateBadges(partial) {
    this.setBadges(partial);
  }

  notifyBadges() {
    const snapshot = this.getBadges();
    this.badgeListeners.forEach((cb) => {
      try {
        cb(snapshot);
      } catch (_) {}
    });
  }

  onBadges(callback) {
    this.badgeListeners.add(callback);
    try {
      callback(this.getBadges());
    } catch (_) {}
    return () => {
      this.badgeListeners.delete(callback);
    };
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const set = this.listeners.get(event);
    if (set.has(callback)) {
      return () => this.off(event, callback);
    }

    set.add(callback);

    const socket = this.connect();
    if (socket) {
      socket.on(event, callback);
    }

    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  disconnect() {
    if (!this.socket) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((cb) => this.socket.off(event, cb));
    });

    this.socket.close();
    this.socket = null;
    this.connected = false;
    this.notifyConnectionState();
  }
}

export default AdminSocketService;
